import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type Ref,
  type RefObject,
  type SetStateAction,
} from "react";
import { Controls, STEPS } from "./StructuredTextBar";
import { Offered } from "./CaretOffer";
import Document from "@tiptap/extension-document";
import { UndoRedo } from "@tiptap/extensions";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import { TextSelection } from "@tiptap/pm/state";
import { closeHistory } from "@tiptap/pm/history";
import type { EditorView } from "@tiptap/pm/view";
import {
  asBlocks,
  asDocument,
  completed,
  textOf,
  type BlockControl,
  type Offer,
  type ReferenceChoice,
  type ReferenceMark,
  type ReferenceSource,
  type StructuredTextFeatures,
  type TextBlock,
} from "@/lib/structured-text";
import {
  BlockClasses,
  BlockMarkers,
  Capacity,
  ClearsBeforeJoining,
  ControlKeys,
  Nesting,
  REDRAW,
  Reference,
  ReferenceTags,
  Stated,
  blockAt,
  offerAt,
} from "@/lib/structured-text-editor";
import { cn } from "@/lib/utils";
import { useEscapeScope } from "./escape-scope";
import { inlineAreaChrome } from "./field-chrome";
import { wrappedIndex } from "@/lib/wrapped-index";

export type {
  BlockControl,
  ReferenceChoice,
  ReferenceMark,
  ReferenceSource,
  StructuredTextFeatures,
};

/**
 * The words that name something, tinted so the writer can see which they are.
 *
 * The brand tint rather than the blue the prototype underlays: blue there marks
 * language that could be broadened, and two features wearing one colour would
 * read as one. A tint alone, because the field already carries the design's
 * dotted underline and a second one under these words would read as an error.
 */
export const REFERENCE_TINT = "rounded-[3px] bg-muted px-[1px]";

/**
 * A named stretch read rather than written: its words, its tag after them, and
 * what the tag says on hover. A tag with somewhere to go makes the words and the
 * tag one link to it, as v0.28 makes a claim's component name and numeral one.
 *
 * ``tinted`` is the editor's tint, for a page where the words can be written in
 * elsewhere. A summary that only reads them shows them plain, the tag in the
 * words' own weight, as v0.28's review before generating does.
 */
export function ReferenceWords({
  reference,
  references,
  tinted = true,
  children,
}: {
  reference: string;
  references: Pick<ReferenceSource, "tagOf" | "markOf">;
  tinted?: boolean;
  children: ReactNode;
}) {
  const tag = references.tagOf(reference) ?? undefined;
  const mark = references.markOf?.(reference) ?? null;
  const shown = {
    "data-reference": reference,
    "data-reference-tag": tag,
    "data-reference-unconfirmed": mark?.unconfirmed ? "true" : undefined,
    "data-reference-plain": tinted ? undefined : "true",
    title: mark?.title,
  };
  if (mark?.open !== undefined && tag !== undefined) {
    return (
      <button
        type="button"
        {...shown}
        onClick={mark.open}
        className={cn(
          tinted && REFERENCE_TINT,
          "cursor-pointer text-left hover:underline focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none",
        )}
      >
        {children}
      </button>
    );
  }
  return (
    <span {...shown} className={tinted ? REFERENCE_TINT : undefined}>
      {children}
    </span>
  );
}

/** The tint, plus the ring that says the whole reference is selected, which only the editor has. */
const WORDS = `${REFERENCE_TINT} [&.ProseMirror-selectednode]:ring-2 [&.ProseMirror-selectednode]:ring-ring/50`;

/**
 * The editor's extensions, rebuilt only when a feature is switched on or off.
 *
 * The reference source is read through a closure on every draw instead of being
 * a dependency, so a source that answers differently redraws the tags without
 * rebuilding the editor and dropping the caret mid-word.
 */
function useExtensions(features: StructuredTextFeatures) {
  const { nesting, capacity } = features;
  const sourceRef = useRef(features.references);
  sourceRef.current = features.references;
  const classRef = useRef(features.blockClass);
  classRef.current = features.blockClass;
  const markersRef = useRef(features.blockMarkers);
  markersRef.current = features.blockMarkers;
  const refusedRef = useRef(capacity?.refused);
  refusedRef.current = capacity?.refused;
  const underRef = useRef(nesting?.under);
  underRef.current = nesting?.under;
  const actsRef = useRef(features.controls?.acts);
  actsRef.current = features.controls?.acts;
  // Every value the set is built from is reduced to something that compares by
  // value first. A consumer writes its features as a literal, so anything read
  // from it by reference is a new thing on every render, and the set would be
  // rebuilt on every keystroke: the editor is then given new options each time,
  // which is real work for no change at all.
  const blocks = capacity?.blocks;
  const deepest = nesting?.deepest;
  const starts = nesting?.under !== undefined;
  return useMemo(
    () => [
      Document,
      // Never a feature: a writer who cannot take back what they just did has
      // no way out of a mistake, and a claim is the attorney's own words.
      UndoRedo,
      Paragraph.extend({
        addAttributes: () => ({
          depth: { default: 0 },
          numbered: { default: false },
        }),
      }),
      Text,
      Stated(),
      Reference(WORDS),
      ReferenceTags(() => sourceRef.current),
      ...(deepest === undefined
        ? []
        : [
            Nesting(
              deepest,
              starts
                ? (block) => underRef.current?.(block) ?? block.depth
                : undefined,
            ),
            ClearsBeforeJoining(),
          ]),
      // After the nesting, because a later extension is asked about a key
      // before an earlier one. Registered first, the cap was never reached and
      // Enter started the very block it exists to refuse.
      ...(blocks === undefined
        ? []
        : [
            Capacity(blocks, () => {
              refusedRef.current?.();
            }),
          ]),
      ControlKeys(() => actsRef.current ?? [], setBlock),
      BlockClasses((block) => classRef.current?.(block) ?? ""),
      BlockMarkers(() => markersRef.current),
    ],
    [deepest, starts, blocks],
  );
}

/**
 * Moving through what is offered, and taking one.
 *
 * Handled inside the editor rather than on anything around it. ProseMirror acts
 * on the keydown itself, so Enter on a wrapper arrives after a new block has
 * already been started and the offer has closed.
 */
function steer(
  offer: Offer | null,
  at: number,
  setAt: (at: number) => void,
  choose: (choice: ReferenceChoice) => void,
) {
  return (_view: EditorView, event: KeyboardEvent): boolean => {
    if (offer === null) return false;
    // Against the list in hand: a caret moved to a word offering fewer things
    // leaves the row that was highlighted past the end of the new list, and
    // taking it would name nothing.
    const on = Math.min(at, offer.choices.length - 1);
    const step = STEPS[event.key];
    if (step !== undefined) {
      setAt(wrappedIndex(on, step, offer.choices.length));
      return true;
    }
    if (event.key === "Enter" || event.key === "Tab") {
      choose(offer.choices[on]);
      return true;
    }
    return false;
  };
}

/** What the editor is built with, gathered so the component reads as what it draws. */
interface EditorParts {
  /**
   * What the editor starts with, and what it is put back to when the words it
   * is given differ from the words it holds.
   *
   * A served set arriving after the first render replaces what is there, since
   * the editor would otherwise sit on whatever it was built with. The words the
   * writer is typing are not touched: the comparison is against what they have
   * already answered with, so a set that only echoes their own change is not
   * written back over the caret.
   */
  value: readonly TextBlock[];
  label: string;
  features: StructuredTextFeatures;
  onChange: (blocks: TextBlock[]) => void;
  onLeave?: (blocks: TextBlock[]) => void;
  setOffer: (offer: Offer | null) => void;
  setCaretIn: Dispatch<SetStateAction<TextBlock | null>>;
  /** Back to the first choice, since a changed word offers a different list. */
  setAt: (at: number) => void;
  /** What the list of offers is positioned inside, so it opens where the caret is. */
  withinRef: RefObject<HTMLDivElement | null>;
  /** The words the editor last answered with, so a set that echoes them is not reapplied. */
  answeredRef: RefObject<readonly TextBlock[]>;
  steerRef: RefObject<(view: EditorView, event: KeyboardEvent) => boolean>;
}

/** The editor itself: what it holds, what it answers with, and what it tells the screen. */
function useStructuredEditor({
  value,
  label,
  features,
  onChange,
  onLeave,
  setOffer,
  setCaretIn,
  setAt,
  withinRef,
  answeredRef,
  steerRef,
}: EditorParts) {
  const { references } = features;
  // The words the editor opens with. Held rather than rebuilt, because after
  // the first render it is no longer what the editor holds, and handing it a
  // new one on every render is a whole view update for nothing.
  const opened = useRef(asDocument(value)).current;
  // The field keeps the design's inline look unless a bar is drawn around it
  // for good: with a floating bar the claim still reads as a claim, and its
  // lead-in still lines up with its first line.

  // Built once for the same reason. TipTap compares its options by identity on
  // every render and sets them again on any difference, and setting them updates
  // the whole view: a fresh literal is that work on every keystroke of every
  // claim on the page. Nothing in here moves, since the key handler reads what
  // it needs through a ref.
  const props = useMemo(
    () => ({
      handleKeyDown: (view: EditorView, event: KeyboardEvent) =>
        steerRef.current(view, event),
      // ProseMirror's contenteditable carries no role of its own, so a reader
      // is told what it is rather than left to infer it from the editing flag.
      attributes: {
        "aria-label": label,
        role: "textbox",
        "aria-multiline": "true",
        ...(features.placeholder === undefined
          ? {}
          : { "aria-placeholder": features.placeholder }),
        // The surface around it carries the chrome where there is a bar, so
        // the field only takes the focus outline off the browser's hands.
        class: cn("focus-visible:outline-none", inlineAreaChrome()),
      },
    }),
    [label, steerRef, features.placeholder],
  );
  return useEditor({
    extensions: useExtensions(features),
    content: opened,
    editorProps: props,
    onUpdate: ({ editor: changed }) => {
      const written = asBlocks(changed.getJSON());
      answeredRef.current = written;
      onChange(written);
    },
    // Every transaction, not the selection alone: the bar was dead until the
    // caret moved, because a click into a field already focused changes nothing
    // and the block it acts on was still unknown. Only when it differs, since a
    // fresh object on every keystroke re-renders the editor for nothing.
    onTransaction: ({ editor: changed }) => {
      const block = blockAt(changed);
      setCaretIn((was) =>
        was?.depth === block?.depth && was?.numbered === block?.numbered
          ? was
          : block,
      );
    },
    // The one place the offer is worked out. A typed character moves the caret as
    // well as the words, so both this and `onUpdate` are told; asking twice reads
    // the caret's position out of the layout twice for one answer.
    onSelectionUpdate: ({ editor: moved }) => {
      setOffer(
        references === undefined
          ? null
          : offerAt(moved, references, withinRef.current),
      );
      setAt(0);
    },
    onBlur: ({ editor: left }) => {
      setOffer(null);
      onLeave?.(asBlocks(left.getJSON()));
    },
  });
}

/** Set the block the caret is in the way one act says, and stay in the words. */
function setBlock(editor: Editor, control: BlockControl): void {
  const { state, view } = editor;
  const { $from } = state.selection;
  const block = blockAt(editor);
  if (block === null) return;
  view.dispatch(
    state.tr.setNodeMarkup($from.before(), undefined, {
      ...$from.parent.attrs,
      ...control.run(block),
    }),
  );
  editor.commands.focus();
}

/**
 * The chrome around the words: none of its own.
 *
 * The words are drawn as themselves, which is how a claim reads inside its card,
 * and the bar floats over what is above them rather than sitting in a surface
 * with them. Positioned only, so the bar has something to be positioned against.
 */
const SURFACE = "group/text relative";

/**
 * Put the editor back to the words it is given when they differ from the words
 * it holds, and tell it when what its tags are projected from has moved.
 *
 * A served set arriving after the first render replaces what is there, since
 * the editor would otherwise sit on whatever it was built with. What the writer
 * is typing is not touched: the comparison is against what the editor last
 * answered with, so a set that only echoes their own change is not written back
 * over the caret.
 */
function useServedWords(
  editor: Editor,
  value: readonly TextBlock[],
  answeredRef: RefObject<readonly TextBlock[]>,
  features: StructuredTextFeatures,
) {
  useEffect(() => {
    // Never while the writer is in the words. A set arriving mid-sentence is
    // either their own change coming back around, which changes nothing, or
    // somebody else's, and pulling the words out from under a caret to show it
    // would lose what they were part way through saying.
    if (editor.isFocused) return;
    if (JSON.stringify(answeredRef.current) === JSON.stringify(value)) return;
    answeredRef.current = value;
    editor.commands.setContent(asDocument(value), { emitUpdate: false });
  }, [editor, value, answeredRef]);

  // What the tags and markers are projected from can move while the claim sits
  // open: an element pinned to a sheet, a numeral typed over, a different rule.
  // The editor is told, since nothing in the words has changed to tell it. A
  // change in the words redraws them anyway, so this is only for the rest.
  //
  // Never on the first run: the plugins built every decoration from the opening
  // document as they were created, and redrawing here would throw that away and
  // build it again, once per editor, for every claim on the page at once.
  const drawnFromRef = useRef(features.redrawOn);
  useEffect(() => {
    if (drawnFromRef.current === features.redrawOn) return;
    drawnFromRef.current = features.redrawOn;
    editor.view.dispatch(editor.state.tr.setMeta(REDRAW, true));
  }, [editor, features.redrawOn]);
}

/**
 * Write the whole name the writer chose in place of the fragment they typed.
 *
 * Its own step in the history: choosing is a decision, and an undo should take
 * back the decision rather than the words typed towards it as well.
 */
function written(editor: Editor, offer: Offer, choice: ReferenceChoice) {
  const { state, view } = editor;
  const node = state.schema.nodes.reference.create({
    reference: choice.reference,
    // The whole name, not the fragment reached so far: choosing is a decision,
    // and a claim and a drawing calling one part two things reads as two.
    text: completed(offer.text, choice.label),
  });
  // A transaction rather than insertContentAt, which trims the whitespace at
  // the edges of what it replaces and ate the space before the word.
  // replaceWith, not replaceRangeWith: the range spelling is the one that
  // deletes around what it is given to make it fit, and it ate the space
  // before the word every time.
  const change = closeHistory(state.tr.replaceWith(offer.from, offer.to, node));
  // The caret goes after the whole reference. A replacement leaves the node
  // itself selected, and the next keystroke would type over what was chosen.
  view.dispatch(
    change.setSelection(
      TextSelection.create(change.doc, offer.from + node.nodeSize),
    ),
  );
  view.focus();
}

/**
 * The bar to draw right now, or nothing while the writer is elsewhere.
 *
 * Held as state rather than left to focus-within in CSS, which can only hide a
 * bar that is already built. A hidden bar is still a toolbar in the
 * accessibility tree and still in the page's census of controls, and a page of
 * claims would carry one for every claim nobody is writing in.
 *
 * A bar that comes and goes never becomes the surface's chrome either: the
 * field keeps the inline look every other field on the page has, and the bar is
 * drawn over what is above it, so nothing moves when it arrives.
 */
function useBarState(controls: StructuredTextFeatures["controls"]) {
  const [within, setWithin] = useState(false);
  const hasBar = controls !== undefined && controls.acts.length > 0;
  // The bar itself when it is drawn, rather than a flag beside it: one answer,
  // so nothing else can come to disagree about whether there is one.
  return { setWithin, drawn: hasBar && within ? controls : null };
}

function blank(block: TextBlock): boolean {
  return textOf(block).trim() === "";
}

/** The placeholder over an empty field: seen, not read, since the field carries it as aria-placeholder. */
function Prompt({ words, empty }: { words?: string; empty: boolean }) {
  if (words === undefined || !empty) return null;
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute top-px left-0 text-muted-foreground"
    >
      {words}
    </span>
  );
}

/**
 * Escape is only the way out of the field. Closing the offer is the popover scope's, and it
 * answers first, so one press never does both. A writer who reached the words by keyboard
 * needs a way out that is not Tab, which this editor spends on the indent.
 */
function leaveOnEscape(key: string, offerOpen: boolean, editor: Editor): void {
  if (key === "Escape" && !offerOpen) editor.view.dom.blur();
}

/** What a consumer may do to the editor from outside it: put the caret at the end of the words. */
export interface StructuredTextHandle {
  focus: () => void;
}

/** Hand the consumer its handle: TipTap places the caret at the end of the words on the next frame. */
function useCaretHandle(
  ref: Ref<StructuredTextHandle> | undefined,
  editor: Editor,
): void {
  useImperativeHandle(ref, () => ({
    focus: () => {
      editor.commands.focus("end");
    },
  }));
}

/**
 * Text the writer edits, held as blocks of runs rather than as one string.
 *
 * The editor itself holds words and nothing else. Every other behaviour is a
 * feature a consumer asks for, and the things words can name come from a source
 * the consumer gives, so this module names no domain of its own.
 */
export function StructuredText({
  value,
  onChange,
  onLeave,
  features = {},
  label,
  className,
  ref,
}: {
  value: readonly TextBlock[];
  onChange: (blocks: TextBlock[]) => void;
  /** Called when the writer leaves the whole editor, for a consumer that saves then. */
  onLeave?: (blocks: TextBlock[]) => void;
  features?: StructuredTextFeatures;
  label: string;
  className?: string;
  /** For an act outside the words that puts the writer in them, such as a menu's Edit text. */
  ref?: Ref<StructuredTextHandle>;
}) {
  const { controls } = features;
  const [offer, setOffer] = useState<Offer | null>(null);
  // Through the arbiter rather than off this element's own key handler: an
  // Escape meant for the list would otherwise also reach whatever gesture or
  // fullscreen scope is live, and which of them answered would be mount order.
  useEscapeScope("popover", offer !== null, () => {
    setOffer(null);
  });
  const { setWithin, drawn } = useBarState(controls);
  const [caretIn, setCaretIn] = useState<TextBlock | null>(null);
  const withinRef = useRef<HTMLDivElement>(null);
  // The words the editor last answered with, so a served set that only echoes
  // them is not written back over the caret.
  const answeredRef = useRef<readonly TextBlock[]>(value);
  const [at, setAt] = useState(0);
  // Read through a ref because the editor's key handler is built once and would
  // otherwise close over the first render's offer.
  const steerRef = useRef(steer(offer, at, setAt, () => undefined));

  const editor = useStructuredEditor({
    value,
    label,
    features,
    onChange,
    onLeave,
    setOffer,
    setCaretIn,
    setAt,
    withinRef,
    answeredRef,
    steerRef,
  });

  useServedWords(editor, value, answeredRef, features);
  useCaretHandle(ref, editor);

  const choose = (choice: ReferenceChoice) => {
    if (offer === null) return;
    written(editor, offer, choice);
    setOffer(null);
  };
  steerRef.current = steer(offer, at, setAt, choose);

  return (
    <div
      ref={withinRef}
      className={cn(SURFACE, className)}
      onFocus={() => {
        setWithin(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setWithin(false);
      }}
      onKeyDown={(event) => {
        leaveOnEscape(event.key, offer !== null, editor);
      }}
    >
      {drawn !== null && (
        <Controls
          controls={drawn.acts}
          block={caretIn}
          note={drawn.note}
          onRun={(control) => {
            setBlock(editor, control);
          }}
        />
      )}
      <EditorContent editor={editor} />
      <Prompt words={features.placeholder} empty={value.every(blank)} />
      {offer !== null && <Offered offer={offer} at={at} onChoose={choose} />}
    </div>
  );
}

export { textOf };
export type { TextBlock };
